import axios from 'axios';

export interface CelulaMatriz {
  bairro: string;
  tema: string;
  demanda: number | null;
  cobertura: number;
  /** Demanda sem cobertura de ação: onde agir primeiro. */
  lacuna: number | null;
  suprimido: boolean;
}

export interface TemaFalado {
  tema: string;
  /** null quando suprimido por k-anonimato — não é zero. */
  total: number | null;
  abertas: number | null;
  concluidas: number | null;
  bairros: number | null;
  suprimido: boolean;
  /** null quando não há período anterior, ou quando o tema foi suprimido. */
  variacao: number | null;
}

export interface ResumoDashboard {
  contatos: { total: number; com_optin: number; sem_base: number };
  solicitacoes: {
    total: number;
    abertas: number;
    concluidas: number;
    /** Prazo ja passou. Disjunto de `vencem_hoje`: um pedido esta num ou no outro. */
    sla_vencido: number;
    /** Prazo cai hoje e a hora ainda nao passou. */
    vencem_hoje: number;
  };
  acoes: { total: number; em_andamento: number; concluidas: number; investimento: number };
  emendas: { total: number; indicado: number; empenhado: number; pago: number };
  /**
   * Nao existe `felicitados` aqui, e a ausencia e deliberada.
   *
   * O protótipo escreve "3 felicitados pela régua". Existe a entidade de régua
   * com gatilho de aniversário e uma marca de ativa, mas NADA no sistema a
   * dispara: o único disparo real acontece na conclusão de solicitação. Exibir
   * "felicitados" seria afirmar um envio que nunca aconteceu — uma afirmação
   * falsa, que é pior do que um número errado. O que se pode afirmar é quem
   * poderia receber (`com_optin`) e quem exige contato direto (`sem_optin`).
   */
  aniversariantes: { hoje: number; com_optin: number; sem_optin: number };
  liderancas: {
    ativas: number;
    /**
     * `saude` abaixo de 50. E uma nota DIGITADA pelo gabinete, nao um score
     * calculado -- liderança sem nota nao entra, porque ausencia de nota nao e
     * nota baixa.
     */
    precisam_atencao: number;
    visitas_30_dias: number;
  };
  agenda: {
    proximos_7_dias: number;
    hoje: number;
    compromissos_hoje: Array<{
      id: string;
      inicio: string;
      hora: string;
      titulo: string;
      local: string | null;
    }>;
  };
  territorios: { total: number; sem_acao_concluida: number };
  cadastros_recentes: Array<{
    id: string;
    nome: string;
    territorio: string | null;
    base_legal: string | null;
  }>;
  /** `total` null quando suprimido por k-anonimato — não é zero. */
  solicitacoes_por_territorio: Array<{
    territorio: string;
    total: number | null;
    suprimido: boolean;
  }>;
  prioridades: Array<{
    bairro: string;
    necessidade: number;
    solicitacoes_abertas: number | null;
    acoes_concluidas: number;
    suprimido: boolean;
  }>;
}

export type TipoVotoOrfao = 'branco' | 'nulo' | 'abstencao' | 'desistente';

export interface VotoOrfao {
  id: string;
  ciclo: number;
  escopo?: string;
  bairro: string;
  tipo: TipoVotoOrfao;
  candidato?: string;
  votos: number;
  observacao?: string;
}

export interface ResumoOrfaosBairro {
  bairro: string;
  eleitores: number;
  total: number;
  por_tipo: Record<TipoVotoOrfao, number>;
  percentual_eleitorado: number;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const analiseApi = {
  dashboard: async (token: string) => {
    const res = await axios.get<ResumoDashboard>(`${API_URL}/terr/analise/dashboard`, auth(token));
    return res.data;
  },
  matriz: async (token: string) => {
    const res = await axios.get<CelulaMatriz[]>(`${API_URL}/terr/analise/matriz`, auth(token));
    return res.data;
  },
  falando: async (token: string, dias = 90) => {
    const res = await axios.get<TemaFalado[]>(`${API_URL}/terr/analise/falando`, {
      ...auth(token),
      params: { dias },
    });
    return res.data;
  },
};

export const votosOrfaosApi = {
  list: async (token: string, ciclo?: number) => {
    const res = await axios.get<VotoOrfao[]>(`${API_URL}/eleg/votos-orfaos`, {
      ...auth(token),
      params: ciclo ? { ciclo } : undefined,
    });
    return res.data;
  },
  resumo: async (token: string, ciclo?: number) => {
    const res = await axios.get<ResumoOrfaosBairro[]>(`${API_URL}/eleg/votos-orfaos/resumo`, {
      ...auth(token),
      params: ciclo ? { ciclo } : undefined,
    });
    return res.data;
  },
  create: async (data: Partial<VotoOrfao>, token: string) => {
    const res = await axios.post<VotoOrfao>(`${API_URL}/eleg/votos-orfaos`, data, auth(token));
    return res.data;
  },
  update: async (id: string, data: Partial<VotoOrfao>, token: string) => {
    const res = await axios.put<VotoOrfao>(`${API_URL}/eleg/votos-orfaos/${id}`, data, auth(token));
    return res.data;
  },
  delete: async (id: string, token: string) => {
    await axios.delete(`${API_URL}/eleg/votos-orfaos/${id}`, auth(token));
  },
};
